import { FC, ReactNode } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { Button, InlineNotification, Loading } from '@carbon/react';
import { Edit, Launch } from '@carbon/react/icons';
import { Helmet } from 'react-helmet-async';
import { Link, PlainLink } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { PageHeader } from 'components/page-header';
import { pageTitle } from 'utils/page-title';
import { formatResource } from 'utils/misc';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import routes from '~/routes';
import { fmtAmount } from '../format';
import { RoyaltyCheckLineItemsTable } from '../shared/royalty-check-line-items-table';
import { useRoyaltyCheckAtom } from './atoms';
import { RoyaltyCheckSupportingFiles } from './supporting-files';
import { SourceDocumentBanner } from './source-document-banner';
import styles from './page.module.scss';

type AssociatedPropertyRow = {
  id: string;
  name: string | null;
  uwi: string | null;
  wellHref: string | null;
  lineItemCount: number;
  allocatedAmount: number;
} & Record<string, unknown>;

const associatedPropertyColumns: DataTableColumnDef<AssociatedPropertyRow>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Property Name',
    cell: ({ row }) => row.original.name ?? '--',
  },
  {
    id: 'uwi',
    accessorKey: 'uwi',
    header: 'UWI',
    cell: ({ row }) => {
      const { uwi, wellHref } = row.original;
      if (!uwi) return '--';
      return wellHref ? <Link to={wellHref}>{uwi}</Link> : uwi;
    },
  },
  {
    id: 'lineItemCount',
    accessorKey: 'lineItemCount',
    header: 'Count of Line Items',
    meta: { type: 'integer', align: 'right' },
  },
  {
    id: 'allocatedAmount',
    accessorKey: 'allocatedAmount',
    header: 'Property Allocated Check Amount',
    meta: { type: 'decimal', align: 'right' },
    cell: ({ row }) => fmtAmount(row.original.allocatedAmount),
  },
];

const Detail: FC<{ label: string; value: ReactNode }> = ({ label, value }) => {
  const empty =
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.length === 0);
  return (
    <div className={styles.detail}>
      <dt className={styles.detailLabel}>{label}</dt>
      <dd className={styles.detailValue}>{empty ? '--' : value}</dd>
    </div>
  );
};

const RoyaltyCheckShowPage: FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const history = useHistory();
  const [rcResource] = useAtom(useRoyaltyCheckAtom(id));
  const { loading, error, data: royaltyCheck } = formatResource(rcResource);

  if (loading && !royaltyCheck) {
    return (
      <div className={styles.loadingWrapper}>
        <Loading description="Loading royalty check..." withOverlay={false} />
      </div>
    );
  }

  if (error) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        title="Unable to load royalty check"
        subtitle={error}
      />
    );
  }

  const name = royaltyCheck?.name ?? id;
  const salesforceUrl = royaltyCheck?.salesforceHref ?? null;
  // Provisioned checks carry a source document → the primary action is Edit
  // (opens RC Doc Structuring); SF-sourced checks fall back to View in Salesforce.
  const sourceDocument = royaltyCheck?.sourceDocument ?? null;

  return (
    <>
      <Helmet>
        <title>{pageTitle(name)}</title>
      </Helmet>
      <PageHeader
        breadcrumbs={[
          [routes.royaltyChecksPath, 'Royalty Checks'],
          ['', name],
        ]}
        title={name}
        actions={
          sourceDocument ? (
            <Button
              kind="primary"
              size="md"
              renderIcon={Edit}
              onClick={() =>
                history.push({
                  pathname: `/royalty_check_documents/${sourceDocument.id}`,
                })
              }
            >
              Edit
            </Button>
          ) : salesforceUrl ? (
            <Button
              kind="tertiary"
              size="md"
              renderIcon={Launch}
              href={salesforceUrl}
              target="_blank"
              rel="noreferrer"
            >
              View in Salesforce
            </Button>
          ) : null
        }
      />

      <div className={styles.pageContent}>
        {/* Auto-Connect provenance banner (Provisioned checks only); mirrors the
            AFE detail page. Null for Salesforce-sourced checks. */}
        <SourceDocumentBanner
          sourceDocument={royaltyCheck?.sourceDocument ?? null}
        />

        {/* Epic 3 conflict banner: a Salesforce-sourced duplicate shares this
            provisioned check's conflict key and needs examiner review. */}
        {royaltyCheck?.conflictStatus === 'needs_review' && (
          <InlineNotification
            lowContrast
            kind="warning"
            hideCloseButton
            title="Needs review"
            subtitle="A Salesforce-sourced royalty check shares this check's conflict key. Reconcile the duplicate in Salesforce."
          />
        )}

        {/* ─── Royalty Check Details ─── */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Royalty Check Details</h3>
          <dl className={styles.detailsGrid}>
            <Detail label="Check Number" value={royaltyCheck?.checkNumber} />
            <Detail
              label="Check Amount"
              value={fmtAmount(royaltyCheck?.checkAmount ?? null)}
            />
            <Detail
              label="Check Issue Date"
              value={royaltyCheck?.checkIssueDate}
            />
            <Detail label="Owner Number" value={royaltyCheck?.ownerNumber} />
            <Detail label="Source" value={royaltyCheck?.source} />
            <Detail label="Operator" value={royaltyCheck?.operatorName} />
            <Detail
              label="Payor"
              value={
                royaltyCheck?.payor?.name ? (
                  royaltyCheck.payor.entityHref ? (
                    <Link to={royaltyCheck.payor.entityHref}>
                      {royaltyCheck.payor.name}
                    </Link>
                  ) : royaltyCheck.payor.salesforceHref ? (
                    <PlainLink
                      href={royaltyCheck.payor.salesforceHref}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {royaltyCheck.payor.name}
                    </PlainLink>
                  ) : (
                    royaltyCheck.payor.name
                  )
                ) : (
                  '--'
                )
              }
            />
            <Detail
              label="Payee"
              value={
                royaltyCheck?.payee?.name ? (
                  royaltyCheck.payee.entityHref ? (
                    <Link to={royaltyCheck.payee.entityHref}>
                      {royaltyCheck.payee.name}
                    </Link>
                  ) : royaltyCheck.payee.salesforceHref ? (
                    <PlainLink
                      href={royaltyCheck.payee.salesforceHref}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {royaltyCheck.payee.name}
                    </PlainLink>
                  ) : (
                    royaltyCheck.payee.name
                  )
                ) : (
                  '--'
                )
              }
            />
            <Detail
              label="Allocated Line Items"
              value={royaltyCheck?.allocatedLineItems}
            />
            <Detail label="Owner" value={royaltyCheck?.owner} />
            <Detail label="Created At" value={royaltyCheck?.createdAt} />
            <Detail label="Created By" value={royaltyCheck?.createdBy} />
            <Detail
              label="Last Modified At"
              value={royaltyCheck?.lastModifiedAt}
            />
            <Detail
              label="Last Modified By"
              value={royaltyCheck?.lastModifiedBy}
            />
          </dl>
        </section>

        {/* ─── Royalty Check Line Items ─── */}
        <RoyaltyCheckLineItemsTable
          royaltyCheckId={id}
          persistenceKey={`royalty-check-${id}-line-items`}
          initialPageSize={DEFAULT_PAGE_SIZE}
          enableColumnFilters
          className={styles.section}
          searchPlaceholder="Search Line Items"
          renderHeading={(totalCount) => (
            <h3 className={styles.sectionTitle}>
              {`Royalty Check Line Items (${totalCount})`}
            </h3>
          )}
        />

        {/* ─── Associated Properties ─── */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {`Associated Properties (${
              royaltyCheck?.associatedProperties?.length ?? 0
            })`}
          </h3>
          {(royaltyCheck?.associatedProperties?.length ?? 0) === 0 ? (
            <p className={styles.emptyState}>
              No properties associated with this royalty check.
            </p>
          ) : (
            <DataTable<AssociatedPropertyRow>
              data={
                (royaltyCheck?.associatedProperties ??
                  []) as AssociatedPropertyRow[]
              }
              columns={associatedPropertyColumns}
              getRowId={(row) => row.id}
              persistenceKey={`royalty-check-${id}-associated-properties`}
              scroll={{ mode: 'paginated' }}
              initialTableState={{ page: 1 }}
              config={{
                globalSearch: true,
                sorting: true,
                columnFilters: true,
                columnReordering: false,
                export: false,
              }}
              searchPlaceholder="Search Associated Properties"
            />
          )}
        </section>

        {/* ─── Supporting Files ─── */}
        {royaltyCheck?.resources?.supportingFiles?.href && (
          <RoyaltyCheckSupportingFiles
            href={royaltyCheck.resources.supportingFiles.href}
          />
        )}
      </div>
    </>
  );
};

export { RoyaltyCheckShowPage };
